import React, { useState } from "react";
import {
    Autocomplete,
    TextField,
    Button,
    Box,
    Chip,
    TextFieldProps,
    AutocompleteProps,
    Typography
} from "@mui/material";
import CustomizedCheckbox from "../CustomCheckBox";

// Props interface for the component
interface OzAutoCompleteMuiProps<T> {
    /** Array of options (strings or objects) */
    options: T[];
    /** Selected values array */
    value: T[];
    /** Callback when selection changes */
    onChange?: (newValue: T[]) => void;
    /** Current input value for controlled input */
    inputValue?: string;
    /** Placeholder text */
    placeholder?: string;
    /** Function to get label from option (for objects) */
    getOptionLabel?: (option: T) => string;
    /** Function to get unique key from option */
    getOptionKey?: (option: T) => string | number;
    /** Function to compare options */
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    /** Whether the autocomplete is disabled */
    disabled?: boolean;
    /** Whether to show error state */
    error?: boolean;
    /** Helper text to display */
    helperText?: string;
    /** Additional props for TextField */
    textFieldProps?: Partial<TextFieldProps>;
    /** Additional props for Autocomplete */
    autocompleteProps?: Partial<Omit<AutocompleteProps<T, true, false, false>,
        'options' | 'value' | 'onChange' | 'renderInput' | 'multiple' | 'renderOption' | 'filterOptions'>>;
    /** Number of tags to show before collapsing */
    limitTags?: number;
    /** Whether to show Select All/Clear All buttons */
    showActionButtons?: boolean;
    /** Whether to show multiple select */
    multiple?: boolean;
    /** Whether to use fixed height (prevents height changes when focused) */
    fixedHeight?: boolean;
    /** Whether to show label */
    label?: string;
}

/**
 * Reusable Multi-Select Autocomplete with Select All/Clear All functionality
 */
function OzAutoCompleteMui<T = string>({
    options = [],
    value = [],
    onChange,
    placeholder = "Select options",
    getOptionLabel = (option: T): string =>
        typeof option === 'string' ? option as string : (option as any)?.label || (option as any)?.name || '',
    getOptionKey = (option: T): string | number =>
        typeof option === 'string' ? option as string :
            (option as any)?.id || (option as any)?.value || (option as any)?.key || option,
    isOptionEqualToValue = (option: T, value: T): boolean =>
        getOptionKey(option) === getOptionKey(value),
    disabled = false,
    error = false,
    helperText = '',
    textFieldProps = {},
    autocompleteProps = {},
    limitTags = 2,
    showActionButtons = true,
    multiple = true,
    fixedHeight = true,
    label = ''
}: OzAutoCompleteMuiProps<T>) {

    const [inputValue, setInputValue] = useState('');

    const handleSelectAll = (e: React.MouseEvent) => {
        try {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Filter options based on current search input
            const filteredOptions = inputValue
                ? options.filter(option =>
                    getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
                )
                : options;

            // Merge current values with filtered options (avoid duplicates)
            const currentKeys = value.map(v => getOptionKey(v));
            const newOptions = filteredOptions.filter(option =>
                !currentKeys.includes(getOptionKey(option))
            );

            const newValue = [...value, ...newOptions];
            onChange?.(newValue);
        } catch (error) {
            console.error("Error in handleSelectAll:", error);
            // Fallback: select all available options
            onChange?.(options);
        }
    };

    const handleClearAll = (e: React.MouseEvent) => {
        try {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Filter options based on current search input
            const filteredOptions = inputValue
                ? options.filter(option =>
                    getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
                )
                : options;

            // Remove only the filtered options from current values
            const newValue = inputValue
                ? value.filter(selectedOption => {
                    const selectedKey = getOptionKey(selectedOption);
                    return !filteredOptions.some(filteredOption =>
                        getOptionKey(filteredOption) === selectedKey
                    );
                })
                : []; // If no search input, clear all

            onChange?.(newValue);
        } catch (error) {
            console.error("Error in handleClearAll:", error);
            // Fallback: clear all
            onChange?.([]);
        }
    };

    const filterOptions = (options: T[], { inputValue }: { inputValue: string }) => {
        // Filter regular options based on input
        const filtered = inputValue
            ? options.filter(option => {
                if (option === ("ACTION_BUTTONS_ROW" as unknown as T)) return false;
                return getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase());
            })
            : options.filter(option => option !== ("ACTION_BUTTONS_ROW" as unknown as T));

        // Insert the action buttons row at the beginning if we have options and action buttons are enabled
        if (filtered.length > 0 && showActionButtons) {
            filtered.unshift("ACTION_BUTTONS_ROW" as unknown as T);
        }

        return filtered;
    };

    const renderOption = (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T | string,
        { selected }: { selected: boolean }
    ) => {
        // Special handling for the action buttons row
        if (option === "ACTION_BUTTONS_ROW") {
            return (
                <li
                    {...props}
                    style={{
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        borderBottom: '1px solid #E5E5E5',
                        height: 'auto',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <Button
                        sx={{
                            textTransform: 'none',
                            flex: 1,
                            justifyContent: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            color: '#3D8BF8',
                            borderRight: '1px solid #E5E5E5',
                            minWidth: 'auto',
                            borderRadius: 0,
                            '&:hover': {
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                        onClick={handleSelectAll}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        Select All
                    </Button>
                    <Button
                        sx={{
                            textTransform: 'none',
                            flex: 1,
                            justifyContent: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            color: '#3D8BF8',
                            minWidth: 'auto',
                            borderRadius: 0,
                            '&:hover': {
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                        onClick={handleClearAll}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        Clear All
                    </Button>
                </li>
            );
        }

        // Regular option rendering
        return (
            <li {...props} style={{ backgroundColor: "#FFFFFF" }}>
                <CustomizedCheckbox checked={selected} />
                <Box sx={{ pl: 1 }}>
                    {getOptionLabel(option as T)}
                </Box>
            </li>
        );
    };

    const renderTags = (tagValue: T[]) => {
        if (tagValue && Array.isArray(tagValue) && tagValue.length > 0) {
            return tagValue.map((item, index) => (
                <Chip
                    key={getOptionKey(item)}
                    label={getOptionLabel(item)}
                    size="small"
                    sx={{
                        ml: 0.4,
                        mt: 0.2,
                        mb: 0.2,
                        height: '20px',
                        fontSize: '12px',
                        '& .MuiChip-label': {
                            px: 0.5,
                            lineHeight: '18px'
                        },
                        '& .MuiChip-deleteIcon': {
                            fontSize: '14px',
                            width: '16px',
                            height: '16px'
                        }
                    }}
                />
            ));
        }
        return null;
    };

    return (<>
        {label && (
            <Typography
                fontSize='12px'
                fontWeight={400}
                lineHeight='14px'
                marginBottom={'6px'}
                marginLeft='1px'
                color={'#212121'}
            >
                {label}
            </Typography>
        )}
        <Autocomplete<T, true, false, false>
            multiple={multiple as true}
            options={options}
            value={value}
            inputValue={inputValue}
            onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
            onChange={(e, newValue) => onChange?.(newValue)}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            disabled={disabled}
            limitTags={limitTags}
            disableCloseOnSelect={true}
            filterOptions={filterOptions}
            renderOption={renderOption}
            renderTags={renderTags}
            componentsProps={{
                paper: {
                    sx: {
                        border: "1px solid #D6D6D7",
                        marginTop: "6px",
                        borderRadius: "8px"
                    }
                }
            }}
            sx={{
                minWidth: '260px',
                "& .MuiAutocomplete-listbox": {
                    "& li": {
                        backgroundColor: "#FFFFFF !important"
                    },
                    "& li:hover": {
                        backgroundColor: "#f5f5f5 !important"
                    }
                },
                ...(fixedHeight && {
                    "& .MuiAutocomplete-inputRoot": {
                        minHeight: "44px",
                        maxHeight: "60px",
                        overflow: "hidden"
                    },
                    "& .MuiAutocomplete-tag": {
                        margin: "2px 4px 2px 0",
                        maxHeight: "20px"
                    }
                }),
                ...autocompleteProps?.sx
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    fullWidth
                    size="small"
                    error={error}
                    helperText={helperText}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontSize: "14px",
                            color: "#3D4C5E",
                            borderRadius: '8px',
                            minHeight: "44px",
                            ...(fixedHeight && {
                                maxHeight: "60px",
                                overflow: "hidden",
                                '& .MuiAutocomplete-input': {
                                    padding: "8px 14px"
                                }
                            })
                        },
                        minHeight: "44px",
                        ...(fixedHeight && {
                            maxHeight: "60px"
                        }),
                        ...textFieldProps?.sx
                    }}
                    {...textFieldProps}
                />
            )}
            {...autocompleteProps}
        />
    </>
    );
}

export default OzAutoCompleteMui;

// Type definitions for common use cases
export type StringOption = string;

export interface ObjectOption {
    id: string | number;
    label?: string;
    name?: string;
    value?: string | number;
    [key: string]: any;
}
